import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { useDarkMode } from '../context/DarkModeContext';
import { 
  Crown, 
  Trophy, 
  Zap, 
  Award, 
  Edit,
  TrendingUp,
  Star,
  Medal,
  BookOpen,
  Mic,
  Users,
  UserPlus,
  UserMinus,
  MessageSquare
} from 'lucide-react';
import api from '../services/api';

const Profile = () => {
  const navigate = useNavigate();
  const { user, loading } = useUser();
  const { isDarkMode } = useDarkMode();
  const [activeTab, setActiveTab] = useState('overview');
  const [friends, setFriends] = useState([]);
  const [friendsLoading, setFriendsLoading] = useState(false);

  useEffect(() => {
    if (activeTab === 'friends') {
      loadFriends();
    }
  }, [activeTab]);

  const loadFriends = async () => {
    setFriendsLoading(true);
    try {
      const response = await api.getFriends();
      setFriends(response.data || []);
    } catch (error) {
      console.error('Error loading friends:', error);
    } finally {
      setFriendsLoading(false);
    }
  };

  const handleFriendAction = async (action, userId) => {
    try {
      switch (action) {
        case 'remove':
          if (window.confirm('Are you sure you want to remove this friend?')) {
            await api.removeFriend(userId);
            await loadFriends(); // Reload friends list
          }
          break;
        case 'message':
          // Navigate to chat
          navigate('/chat');
          break;
      }
    } catch (error) {
      console.error('Friend action error:', error);
    }
  };

  // Show loading only if we're actually loading and there's a token
  if (loading && localStorage.getItem('token')) {
    return (
      <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900 text-gray-100' : 'bg-gray-50'} flex items-center justify-center`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-2 text-gray-600 dark:text-gray-300">Loading...</p>
        </div>
      </div>
    );
  }

  // If no user and no token, redirect to login
  if (!user) {
    navigate('/login', { state: { from: '/profile' } });
    return null;
  }

  // Use actual user data with fallbacks
  const userProfile = {
    username: user?.username || 'User',
    email: user?.email || 'user@example.com',
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    bio: user?.bio || 'No bio available',
    location: user?.location || 'Location not set',
    level: user?.level || 1,
    xp: user?.xp || 0,
    xpToNext: ((user?.level || 1) * 1000) - (user?.xp || 0),
    crowns: user?.crowns || 0,
    trophies: user?.trophies || 0,
    streak: user?.streak || 0,
    joinDate: user?.joinDate ? new Date(user.joinDate).toISOString().split('T')[0] : '2024-01-01',
    totalDebates: user?.totalDebates || 0,
    winRate: user?.totalDebates > 0 ? Math.round((user?.debatesWon || 0) / user.totalDebates * 100) : 0,
    achievements: user?.achievements?.length || 0,
    rank: user?.level >= 20 ? 'Elite' : user?.level >= 15 ? 'Master' : user?.level >= 10 ? 'Expert' : 'Novice'
  };

  // Get user initials for profile image
  const getUserInitials = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();
    } else if (user?.username) {
      return user.username.charAt(0).toUpperCase();
    }
    return 'U';
  };

  // Use real user data for achievements, activity, and history
  const achievements = Array.isArray(user?.achievements) ? user.achievements : [];

  const recentActivity = Array.isArray(user?.recentActivity) ? user.recentActivity : [];

  const currentGoals = Array.isArray(user?.currentGoals) ? user.currentGoals : [];

  const debateHistory = Array.isArray(user?.debateHistory) ? user.debateHistory : [];

  const stats = [
    {
      label: 'Total Debates',
      value: userProfile.totalDebates,
      icon: Mic,
      color: 'text-blue-600'
    },
    {
      label: 'Win Rate',
      value: `${userProfile.winRate}%`,
      icon: TrendingUp,
      color: 'text-green-600'
    },
    {
      label: 'Achievements',
      value: userProfile.achievements,
      icon: Star,
      color: 'text-yellow-600'
    },
    {
      label: 'Current Rank',
      value: userProfile.rank,
      icon: Medal,
      color: 'text-purple-600'
    }
  ];

  const getRankColor = (rank) => {
    switch (rank) {
      case 'Elite':
        return 'bg-gradient-to-r from-purple-500 to-pink-500';
      case 'Master':
        return 'bg-gradient-to-r from-blue-500 to-purple-500';
      case 'Expert':
        return 'bg-gradient-to-r from-green-500 to-blue-500';
      default:
        return 'bg-gradient-to-r from-gray-500 to-gray-600';
    }
  };

  const FriendCard = ({ friend }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3 flex-1 min-w-0">
          <div className="w-12 h-12 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
            {friend.profileImage ? (
              <img src={friend.profileImage} alt={friend.username} className="w-12 h-12 rounded-full object-cover" />
            ) : (
              <span>{friend.username.charAt(0).toUpperCase()}</span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <Link 
              to={`/profile/${friend._id}`}
              className="block hover:underline"
            >
              <h3 className="font-semibold text-gray-900 dark:text-white truncate hover:text-primary-600 dark:hover:text-primary-400">
                {friend.firstName && friend.lastName 
                  ? `${friend.firstName} ${friend.lastName}` 
                  : friend.username
                }
              </h3>
            </Link>
            <p className="text-sm text-gray-500 dark:text-gray-400">@{friend.username}</p>
            {friend.bio && (
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 line-clamp-2">{friend.bio}</p>
            )}
          </div>
        </div>
        
        <div className="flex flex-col items-end space-y-1 ml-3 min-w-[80px] flex-shrink-0">
          <button
            onClick={() => handleFriendAction('message', friend._id)}
            className="flex items-center justify-center space-x-1 px-2 py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors w-full"
          >
            <MessageSquare className="w-3 h-3" />
            <span>Message</span>
          </button>
          <button
            onClick={() => handleFriendAction('remove', friend._id)}
            className="flex items-center justify-center space-x-1 px-2 py-1.5 text-xs bg-red-500 text-white rounded hover:bg-red-600 transition-colors w-full"
          >
            <UserMinus className="w-3 h-3" />
            <span>Remove</span>
          </button>
        </div>
      </div>

      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-1">
              <Zap className="w-4 h-4 text-yellow-500" />
              <span className="text-gray-600 dark:text-gray-300">{friend.xp || 0} XP</span>
            </div>
            <div className="flex items-center space-x-1">
              <Crown className="w-4 h-4 crown-icon" />
              <span className="text-gray-600 dark:text-gray-300">{friend.crowns || 0}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Trophy className="w-4 h-4 trophy-icon" />
              <span className="text-gray-600 dark:text-gray-300">{friend.trophies || 0}</span>
            </div>
          </div>
          <div className="text-gray-500 dark:text-gray-400">
            Level {friend.level || 1}
          </div>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900 text-gray-100' : 'bg-gray-50 text-gray-900'} pt-24 pb-8`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Profile Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card mb-8"
        >
          <div className="flex flex-col md:flex-row items-center md:items-start space-y-6 md:space-y-0 md:space-x-8">
            {/* Avatar and Basic Info */}
            <div className="text-center md:text-left">
              <div className="w-24 h-24 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full flex items-center justify-center text-white text-3xl font-bold mb-4">
                {user?.profileImage ? (
                  <img 
                    src={user.profileImage} 
                    alt="Profile" 
                    className="w-24 h-24 rounded-full object-cover"
                  />
                ) : (
                  getUserInitials()
                )}
              </div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{userProfile.username}</h1>
              <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium text-white ${getRankColor(userProfile.rank)} mb-3`}>
                {userProfile.rank}
              </div>
              
              {/* Profile Information */}
              <div className="text-left space-y-2">
                {userProfile.firstName && userProfile.lastName && (
                  <p className="text-gray-700 dark:text-gray-300">
                    {userProfile.firstName} {userProfile.lastName}
                  </p>
                )}
                {userProfile.location && userProfile.location !== 'Location not set' && (
                  <p className="text-gray-700 dark:text-gray-300">
                    {userProfile.location}
                  </p>
                )}
                {userProfile.bio && userProfile.bio !== 'No bio available' && (
                  <p className="text-gray-700 dark:text-gray-300 max-w-xs">
                    <span className="font-medium">Bio:</span> {userProfile.bio}
                  </p>
                )}
              </div>
            </div>

            {/* Stats Overview */}
            <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-4">
              {stats.map((stat, index) => (
                <div key={stat.label} className="text-center">
                  <div className={`text-2xl font-bold ${stat.color} mb-1`}>{stat.value}</div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">{stat.label}</div>
                </div>
              ))}
            </div>

            {/* Edit Profile Button */}
            <button 
              className="btn-outline flex items-center"
              onClick={() => navigate('/profile/edit')}
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit Profile
            </button>
          </div>
        </motion.div>

        {/* Tab Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex justify-center mb-8"
        >
          <div className="bg-white dark:bg-gray-800 rounded-lg p-1 shadow-sm">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-6 py-2 rounded-md font-medium transition-colors ${
                activeTab === 'overview'
                  ? 'bg-primary-600 text-white'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('friends')}
              className={`px-6 py-2 rounded-md font-medium transition-colors ${
                activeTab === 'friends'
                  ? 'bg-primary-600 text-white'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Friends ({friends.length})
            </button>
            <button
              onClick={() => setActiveTab('achievements')}
              className={`px-6 py-2 rounded-md font-medium transition-colors ${
                activeTab === 'achievements'
                  ? 'bg-primary-600 text-white'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Achievements
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`px-6 py-2 rounded-md font-medium transition-colors ${
                activeTab === 'history'
                  ? 'bg-primary-600 text-white'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Debate History
            </button>
          </div>
        </motion.div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="grid lg:grid-cols-3 gap-8"
          >
            {/* Progress Section */}
            <div className="lg:col-span-2 space-y-6">
              {/* XP Progress */}
              <div className="card">
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <Zap className="w-5 h-5 mr-2 text-yellow-500" />
                  Level Progress
                </h3>
                <div className="mb-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span>Level {userProfile.level}</span>
                    <span>Level {userProfile.level + 1}</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                    <div 
                      className="bg-gradient-to-r from-yellow-400 to-orange-500 h-3 rounded-full transition-all duration-300"
                      style={{ width: `${((userProfile.xp % 1000) / 1000) * 100}%` }}
                    ></div>
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-300 mt-2">
                    {userProfile.xpToNext} XP needed for next level
                  </div>
                </div>
              </div>

              {/* Recent Activity */}
              <div className="card">
                <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
                <div className="space-y-4">
                  {recentActivity.length === 0 ? (
                    <div className="text-gray-500 dark:text-gray-400 text-sm">No recent activity yet.</div>
                  ) : (
                    recentActivity.map((activity) => (
                      <div key={activity.id || activity.date} className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                          <span className="text-2xl">{activity.icon || '📝'}</span>
                        </div>
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">{activity.title}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">{activity.description}</div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Sidebar Stats */}
            <div className="space-y-6">
              {/* Quick Stats */}
              <div className="card">
                <h3 className="text-lg font-semibold mb-4">Quick Stats</h3>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-300">Member since</span>
                    <span className="font-medium text-gray-900 dark:text-white">{new Date(userProfile.joinDate).toLocaleDateString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-300">Current streak</span>
                    <span className="font-medium text-gray-900 dark:text-white flex items-center">
                      <span className="text-[10px] leading-none mr-1">🔥</span>
                      {userProfile.streak} days
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-300">Total crowns</span>
                    <span className="font-medium flex items-center text-gray-900 dark:text-white">
                      <Crown className="w-4 h-4 mr-1 text-yellow-500" />
                      {userProfile.crowns}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-300">Trophies won</span>
                    <span className="font-medium flex items-center text-gray-900 dark:text-white">
                      <Trophy className="w-4 h-4 mr-1 text-amber-600" />
                      {userProfile.trophies}
                    </span>
                  </div>
                </div>
              </div>

              {/* Goals */}
              <div className="card">
                <h3 className="text-lg font-semibold mb-4">Current Goals</h3>
                <div className="space-y-3">
                  {currentGoals.length === 0 ? (
                    <div className="text-gray-500 dark:text-gray-400 text-sm">No goals set yet.</div>
                  ) : (
                    currentGoals.map((goal) => (
                      <div key={goal.id} className="flex items-center justify-between">
                        <span className="text-sm text-gray-900 dark:text-white">{goal.title}</span>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {goal.progress}/{goal.target}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Friends Tab */}
        {activeTab === 'friends' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="card">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">My Friends</h3>
                <Link
                  to="/friends"
                  className="btn-primary flex items-center"
                >
                  <Users className="w-4 h-4 mr-2" />
                  Manage Friends
                </Link>
              </div>
              
              {friendsLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                  <p className="mt-2 text-gray-600 dark:text-gray-300">Loading friends...</p>
                </div>
              ) : friends.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                  <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Friends Yet</h4>
                  <p className="text-gray-500 dark:text-gray-400 mb-4">
                    Start connecting with other debaters to build your network!
                  </p>
                  <Link
                    to="/friends"
                    className="btn-primary"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Find Friends
                  </Link>
                </div>
              ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {friends.map((friend) => (
                    <FriendCard key={friend._id} friend={friend} />
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Achievements Tab */}
        {activeTab === 'achievements' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="card">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Achievements</h3>
              {achievements.length === 0 ? (
                <div className="text-center py-8">
                    <Award className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                    <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Achievements Yet</h4>
                    <p className="text-gray-500 dark:text-gray-400">Start debating and completing training modules to earn achievements!</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {achievements.map((achievement, index) => (
                    <div
                      key={achievement.id}
                      className={`border rounded-lg p-4 transition-all duration-200 ${
                        achievement.earned
                            ? 'border-green-200 dark:border-green-700 bg-green-50 dark:bg-green-900/20'
                            : 'border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 opacity-50'
                      }`}
                    >
                      <div className="flex items-center space-x-3 mb-3">
                        <span className="text-2xl">{achievement.icon}</span>
                        <div className="flex-1">
                            <h4 className="font-semibold text-gray-900 dark:text-white">{achievement.name}</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-300">{achievement.description}</p>
                        </div>
                        {achievement.earned && (
                          <Award className="w-5 h-5 text-green-600" />
                        )}
                      </div>
                      {achievement.earned && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                          Earned {new Date(achievement.date).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Debate History Tab */}
        {activeTab === 'history' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="card">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Debate History</h3>
              {debateHistory.length === 0 ? (
                <div className="text-center py-8">
                  <Mic className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                  <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Debate History Yet</h4>
                  <p className="text-gray-500 dark:text-gray-400">Start participating in debates to see your history here!</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-gray-700">
                        <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Date</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Topic</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Opponent</th>
                        <th className="text-center py-3 px-4 font-semibold text-gray-900 dark:text-white">Format</th>
                        <th className="text-center py-3 px-4 font-semibold text-gray-900 dark:text-white">Result</th>
                        <th className="text-center py-3 px-4 font-semibold text-gray-900 dark:text-white">Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {debateHistory.map((debate) => (
                        <tr key={debate.id} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
                          <td className="py-4 px-4 text-sm text-gray-900 dark:text-gray-100">
                            {new Date(debate.date).toLocaleDateString()}
                          </td>
                          <td className="py-4 px-4">
                            <div className="font-medium text-gray-900 dark:text-white">{debate.topic}</div>
                          </td>
                          <td className="py-4 px-4 text-gray-600 dark:text-gray-300">{debate.opponent}</td>
                          <td className="py-4 px-4 text-center text-sm text-gray-600 dark:text-gray-300">{debate.format}</td>
                          <td className="py-4 px-4 text-center">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              debate.result === 'Win' 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {debate.result}
                            </span>
                          </td>
                          <td className="py-4 px-4 text-center font-medium">{debate.score}/10</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Profile;

 